import React from 'react';
import { Skeleton, Stack, Typography } from '@mui/material';
import ArrowBackRounded from '@mui/icons-material/ArrowBackRounded';
import DoNotDisturbOnRounded from '@mui/icons-material/DoNotDisturbOnRounded';
import HourglassBottomRounded from '@mui/icons-material/HourglassBottomRounded';
import PhoneInTalkRounded from '@mui/icons-material/PhoneInTalkRounded';
import {
  EmptyState, MetaFacts, Modal, StatusChip, Timeline, ToolbarButton, type TimelineStep,
} from '../../../ui-kit';
import {
  callTheCarrier, fetchCcCase, type CcCall, type CcStory,
} from '../../../services/carrierCorrespondenceService';
import AgentName from '../rocco/AgentName';
import { lengthOf, useLive, when } from '../rocco/live';
import { CALLING, SIDE, STATUS, capital, expectedText, money, stalledFor } from './status';

/**
 * One case: what the carrier said, then how it got here.
 *
 * Built as ROCCO's lead story is: one timeline, no panels inside the modal, and
 * each call's words behind "Show Transcript", which opens them in their own
 * window with a way back. The one addition is the line at the top, what is
 * outstanding and whose move it is, because that is what this agent exists to
 * find and nobody should have to read a timeline to get to it.
 *
 * Opened with a `callId`, as Conversations does, it goes straight to that call's
 * words, the way ROCCO's opens on a call.
 */
export default function CaseStory({ caseId, callId: startOn, onClose }: {
  caseId: string; callId?: string; onClose: () => void;
}) {
  const fetcher = React.useCallback(() => fetchCcCase(caseId), [caseId]);
  // Quick, because this is where a call is watched happening.
  const { data: story, error, reload } = useLive(fetcher, 3000);
  const [callId, setCallId] = React.useState<string | null>(startOn ?? null);
  const [asking, setAsking] = React.useState(false);
  const [problem, setProblem] = React.useState<string | null>(null);

  const found = story?.case;
  const running = found?.status === 'calling' || found?.status === 'call_ended';
  const openCall = callId && story ? story.calls.find((c) => c.id === callId) : undefined;

  if (openCall && found) {
    return (
      <TranscriptModal
        call={openCall}
        applicant={found.applicant}
        onBack={() => setCallId(null)}
        onClose={onClose}
      />
    );
  }

  const ask = async () => {
    setAsking(true);
    setProblem(null);
    try {
      await callTheCarrier(caseId);
      await reload();
    } catch (err) {
      // The message is the useful part (no number on file, a call already
      // running), so it is shown as it came back.
      setProblem(err instanceof Error ? err.message : 'Could not start the call');
    } finally {
      setAsking(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={found ? found.applicant : 'Loading…'}
      subtitle={found
        ? [found.policy_num, found.carrier, found.product].filter(Boolean).join(' · ')
        : undefined}
      actions={found ? <StatusChip {...(STATUS[found.status] ?? STATUS.waiting)} /> : undefined}
      size="md"
      footer={(
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ width: '100%' }}>
          <Typography variant="caption" sx={(t) => ({ color: t.tokens.semantic.danger, minWidth: 0 })}>
            {problem}
          </Typography>
          <Stack direction="row" spacing={1}>
            <ToolbarButton variant="ghost" onClick={onClose}>Close</ToolbarButton>
            {running && found ? (
              <StatusChip tone="good" pulse label={CALLING[found.status as 'calling' | 'call_ended']} />
            ) : (
              <ToolbarButton icon={<PhoneInTalkRounded />} onClick={() => { void ask(); }}
                disabled={asking || !found}>
                Call Carrier
              </ToolbarButton>
            )}
          </Stack>
        </Stack>
      )}
    >
      {error && !story ? <EmptyState message={error} /> : null}
      {!story && !error ? <Timeline steps={[]} loading /> : null}
      {story && found ? (
        <Stack spacing={2}>
          <Finding story={story} />
          <MetaFacts facts={[
            { key: 'stage', label: found.stage },
            { key: 'for', label: `stalled ${stalledFor(found.days_stalled)}, since ${
              found.stage_since
                ? new Date(found.stage_since).toLocaleDateString([], { day: 'numeric', month: 'short', year: 'numeric' })
                : 'an unknown date'}` },
            { key: 'face', label: `${money(found.face_amount)} face amount` },
            ...(found.state ? [{ key: 'state', label: found.state }] : []),
          ]} />
          <Timeline steps={toSteps(story, setCallId)} />
        </Stack>
      ) : null}
    </Modal>
  );
}

/** The line at the top: what the carrier said, or that nobody has asked yet. */
function Finding({ story }: { story: CcStory }) {
  const found = story.case;
  const box = (children: React.ReactNode) => (
    <Stack
      spacing={0.75}
      sx={(t) => ({ p: 1.5, borderRadius: `${t.ds.radius.md}px`, bgcolor: t.tokens.surface.sunken })}
    >
      {children}
    </Stack>
  );

  if (found.status === 'calling' || found.status === 'call_ended') {
    return box(
      <Stack direction="row" spacing={1} alignItems="center">
        <StatusChip tone="good" pulse label={found.status === 'calling' ? 'On the phone' : 'Reading the call'} />
        <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.secondary })}>
          {found.status === 'calling'
            ? `Ringing ${found.carrier} to ask what is outstanding. This updates by itself.`
            : 'The call has ended. Reading what was said.'}
        </Typography>
      </Stack>,
    );
  }

  if (found.status === 'needs_person') {
    return box(
      <>
        <Typography variant="overline" sx={(t) => ({ color: t.tokens.semantic.danger })}>Needs a person</Typography>
        <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary })}>
          {found.reason ?? 'The call could not settle this case.'}
        </Typography>
      </>,
    );
  }

  if (found.blocking_item || found.reason) {
    return box(
      <>
        <Typography variant="overline" sx={(t) => ({ color: t.tokens.text.tertiary })}>What is outstanding</Typography>
        <Typography variant="subtitle1" sx={(t) => ({ color: t.tokens.text.primary })}>
          {capital(found.blocking_item || found.reason)}
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
          {found.blocking_side ? <StatusChip {...SIDE[found.blocking_side]} /> : null}
          <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.secondary })}>
            {expectedText(found.carrier, found.expected_by) ?? `${found.carrier} did not give a date for it to move`}
          </Typography>
        </Stack>
        {found.rep_name || found.reference_number ? (
          <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary })}>
            {[found.rep_name ? `Told to us by ${found.rep_name} at ${found.carrier}` : null,
              found.reference_number ? `reference ${found.reference_number}` : null,
            ].filter(Boolean).join(', ')}
          </Typography>
        ) : null}
      </>,
    );
  }

  return box(
    <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.secondary })}>
      Nobody has called {found.carrier} about this yet. Press <strong>Call Carrier</strong> and the AI will
      ring them and ask what is outstanding.
    </Typography>,
  );
}

/** The call a step is about: the latest one that had started by then. */
function callFor(story: CcStory, at: string | null): CcCall | undefined {
  if (!at) return undefined;
  const moment = new Date(at).getTime();
  return story.calls.find((call) => call.started_at && new Date(call.started_at).getTime() <= moment + 1000);
}

function toSteps(story: CcStory, openCall: (id: string) => void): TimelineStep[] {
  return story.steps.map((step, index) => {
    const key = `${step.kind}-${index}`;
    const meta = when(step.at);
    const call = ['answered', 'no_answer', 'needs_person'].includes(step.kind) ? callFor(story, step.at) : undefined;
    const action = call?.has_transcript ? (
      <ToolbarButton variant="ghost" onClick={() => openCall(call.id)}>Show Transcript</ToolbarButton>
    ) : undefined;

    switch (step.kind) {
      case 'stalled':
        return { key, title: step.label, meta, detail: step.detail ?? undefined, marker: <HourglassBottomRounded /> };
      case 'calling':
      case 'call_ended':
        return {
          key, title: step.label, meta, detail: step.detail ?? undefined,
          marker: <PhoneInTalkRounded />, tone: 'brand',
        };
      case 'answered':
        return {
          key, title: step.label, detail: step.detail ?? undefined, marker: <PhoneInTalkRounded />, tone: 'good',
          meta: call?.duration_secs ? `${meta} · ${lengthOf(call.duration_secs)}` : meta, action,
        };
      case 'no_answer':
        return { key, title: step.label, meta, detail: step.detail ?? undefined, marker: <PhoneInTalkRounded />, action };
      case 'needs_person':
        return {
          key, title: step.label, meta, detail: step.detail ?? undefined,
          marker: <DoNotDisturbOnRounded />, tone: 'bad', action,
        };
      default:
        return { key, title: step.label, meta, detail: step.detail ?? undefined, marker: (step.actor || '·').charAt(0) };
    }
  });
}

/** One call's words, in their own window, with a way back to the case. */
function TranscriptModal({ call, applicant, onBack, onClose }: {
  call: CcCall; applicant: string; onBack: () => void; onClose: () => void;
}) {
  return (
    <Modal
      open
      onClose={onClose}
      title={applicant}
      subtitle={[when(call.started_at), call.duration_secs ? lengthOf(call.duration_secs) : null]
        .filter(Boolean).join(' · ')}
      size="lg"
      actions={<StatusChip tone={call.live ? 'good' : 'neutral'} pulse={call.live}
        label={call.live ? 'On the call' : 'Call ended'} />}
      footer={(
        <Stack direction="row" justifyContent="space-between" sx={{ width: '100%' }}>
          <ToolbarButton variant="ghost" onClick={onBack} icon={<ArrowBackRounded />}>Back to the case</ToolbarButton>
          <ToolbarButton variant="ghost" onClick={onClose}>Close</ToolbarButton>
        </Stack>
      )}
    >
      <Stack spacing={1.5}>
        {call.agent ? (
          <AgentName person={{ name: call.agent, kind: 'ai' }} subtitle={call.outcome || undefined} />
        ) : null}
        {call.summary ? (
          <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.secondary })}>{call.summary}</Typography>
        ) : null}
        <Stack spacing={1.25} sx={(t) => ({ p: 1.5, borderRadius: `${t.ds.radius.md}px`, bgcolor: t.tokens.surface.sunken })}>
          {call.transcript.length === 0 ? (
            call.live ? (
              <Stack spacing={1} aria-hidden>
                <Skeleton variant="text" width="60%" />
                <Skeleton variant="text" width="80%" />
              </Stack>
            ) : (
              <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.tertiary })}>
                Nobody spoke on this call, so there is nothing to read.
              </Typography>
            )
          ) : null}
          {call.transcript.map((turn, i) => (
            <Stack key={i} spacing={0.25}>
              <Typography variant="overline" sx={(t) => ({
                color: turn.role === 'agent' ? t.tokens.brand.primary : t.tokens.text.tertiary,
              })}>
                {turn.role === 'agent' ? call.agent : 'The carrier'}
              </Typography>
              <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary })}>{turn.message}</Typography>
            </Stack>
          ))}
        </Stack>
      </Stack>
    </Modal>
  );
}
